import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CartItem, CartSummary } from "@/components/CartItem";
import { CartAddons } from "@/components/CartAddons";
import { CartSetNudge } from "@/components/CartSetNudge";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Trash2, Gift, ShoppingBag, Heart, Truck, CheckCircle2, AlertTriangle } from "lucide-react";
import ghHeart from "@/assets/gh-heart.png";

const SHIP_COUNTRIES = [
  ["US", "United States"], ["CA", "Canada"], ["MX", "Mexico"], ["GB", "United Kingdom"],
  ["ES", "Spain"], ["ID", "Indonesia"], ["HK", "Hong Kong"], ["JM", "Jamaica"],
  ["AU", "Australia"], ["DE", "Germany"], ["FR", "France"], ["PH", "Philippines"],
  ["SG", "Singapore"], ["JP", "Japan"], ["NZ", "New Zealand"], ["IN", "India"],
];

export default function CartPage() {
  const { items, box, updateQty, removeItem, removeBox, total, count, boxTotal } = useCart();
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [email, setEmail] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [giftReceipt, setGiftReceipt] = useState(false);
  const [giftMessage, setGiftMessage] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoPct, setPromoPct] = useState(0);
  const [promoGift, setPromoGift] = useState("");
  const [promoMsg, setPromoMsg] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [donation, setDonation] = useState(0);
  const [donationLabel, setDonationLabel] = useState("");
  const { localEstimate } = useCurrency();
  const [ship, setShip] = useState({ name: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US", phone: "" });
  const [shipQuote, setShipQuote] = useState(null); // { amount, provider, servicelevel, shipping_quote_id, estimated_days }
  const [shipBusy, setShipBusy] = useState(false);
  const [addrCheck, setAddrCheck] = useState(null); // { ok, messages }
  const [familyNumber, setFamilyNumber] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/settings").then((r) => {
      const total = r.data.impact_total ?? r.data.impact_families ?? 0;
      setFamilyNumber(total + 1);
    }).catch(() => {});
  }, []);

  const setShipField = (k, v) => { setShip((s) => ({ ...s, [k]: v })); setShipQuote(null); setAddrCheck(null); };
  const onSelectAddress = (parts) => {
    setShip((s) => ({
      ...s,
      street1: parts.street1 || s.street1,
      city: parts.city || s.city,
      state: parts.state || s.state,
      zip: parts.zip || s.zip,
      country: SHIP_COUNTRIES.some(([c]) => c === parts.country) ? parts.country : s.country,
    }));
    setShipQuote(null); setAddrCheck(null);
  };

  const DONATIONS = [
    { amt: 1, label: "Round up (+$1)" },
    { amt: 3, label: "Add $3 for socks" },
    { amt: 5, label: "Add $5 to the blanket fund" },
    { amt: 10, label: "Add $10 for hygiene supplies" },
  ];

  const discountedTotal = promoPct ? Math.round(total * (1 - promoPct / 100) * 100) / 100 : total;
  const shippingAmt = shipQuote?.amount || 0;
  const finalTotal = Math.round((discountedTotal + donation + shippingAmt) * 100) / 100;
  const pickDonation = (d) => {
    if (donation === d.amt) { setDonation(0); setDonationLabel(""); }
    else { setDonation(d.amt); setDonationLabel(d.label); }
  };
  const hasPhysical = !!box || items.some((i) => i.product_type !== "digital");
  const resetPromo = () => { setPromoPct(0); setPromoGift(""); setPromoMsg(""); };

  useEffect(() => {
    const prefill = localStorage.getItem("gh_promo_prefill");
    if (prefill && count > 0) {
      setPromoCode(prefill);
      localStorage.removeItem("gh_promo_prefill");
      if (agreed) setShowConfirm(true);
      else toast.success(`${prefill} is ready 💛 Agree to our Policies and review your order to apply it.`);
    }
  }, [count]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      resetPromo(); setPromoMsg("Enter your email above to apply a code."); return;
    }
    setPromoBusy(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode.trim(), email: email.trim() });
      setPromoPct(data.percent || 0);
      setPromoGift(data.gift || "");
      setPromoMsg(data.message || "");
    } catch {
      resetPromo(); setPromoMsg("Could not validate that code.");
    } finally {
      setPromoBusy(false);
    }
  };

  const reviewOrder = () => {
    if (!agreed) { toast.error("Please agree to our Policies to continue."); return; }
    setShowConfirm(true);
  };

  const cartLines = () => items.map((i) => ({ product_id: i.id, quantity: i.quantity, recipient: i.recipient || undefined, recipient_name: i.recipient_name || undefined, personal_message: i.personal_message || undefined, delivery_pref: i.delivery_pref || undefined, delivery_address: i.delivery_address || undefined, color: i.color || undefined }));

  const getShippingRate = async () => {
    if (!ship.street1.trim() || !ship.city.trim() || !ship.country) {
      toast.error("Please enter street, city and country to calculate shipping."); return;
    }
    setShipBusy(true);
    try {
      // 1) Validate & normalize the address (best-effort)
      let addr = { ...ship };
      try {
        const { data: v } = await api.post("/shipping/validate", { ...ship, name: ship.name || buyerName || "Recipient", email: email || undefined });
        if (v && v.checked) {
          if (v.normalized) { addr = { ...ship, ...v.normalized }; setShip((s) => ({ ...s, ...v.normalized })); }
          setAddrCheck({ ok: v.is_valid !== false, messages: v.messages || [] });
        }
      } catch { /* validation is non-blocking */ }
      // 2) Fetch the live carrier rate
      const { data } = await api.post("/shipping/quote", {
        items: cartLines(),
        byob: box ? { item_ids: box.items.map((i) => i.id), gift_note: box.gift_note || "" } : null,
        address: { ...addr, name: addr.name || buyerName || "Recipient", email: email || undefined },
      });
      if (data.needs_shipping === false) { setShipQuote({ amount: 0, provider: "", servicelevel: "" }); toast.success("No shipping needed for this order."); }
      else { setShipQuote(data); toast.success(`Shipping: ${money(data.amount)} via ${data.provider} ${data.servicelevel}`); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not fetch a shipping rate. Please check the address.");
    } finally {
      setShipBusy(false);
    }
  };

  const checkout = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Please enter a valid email for your receipt."); return; }
    if (hasPhysical && !shipQuote) { toast.error("Please calculate shipping for your address first."); return; }
    setBusy(true);
    try {
      const payload = {
        items: cartLines(),
        byob: box ? { item_ids: box.items.map((i) => i.id), gift_note: box.gift_note || "" } : null,
        origin_url: window.location.origin,
        email: email.trim(),
        buyer_name: buyerName.trim() || undefined,
        promo_code: (promoPct || promoGift) ? promoCode.trim().toUpperCase() : undefined,
        donation: donation > 0 ? donation : undefined,
        donation_label: donation > 0 ? donationLabel : undefined,
        shipping_quote_id: shipQuote?.shipping_quote_id || undefined,
        gift_receipt: hasPhysical && giftReceipt ? true : undefined,
        gift_message: hasPhysical && giftReceipt && giftMessage.trim() ? giftMessage.trim() : undefined,
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  if (count === 0) return (
    <div className="max-w-2xl mx-auto px-5 py-32 text-center">
      <ShoppingBag className="h-12 w-12 text-[#D1CCC5] mx-auto mb-5" />
      <h1 className="font-heading text-4xl text-[#3A3632]">Your cart is empty.</h1>
      <p className="text-[#6B655F] mt-3">Let's find a gift that says exactly what their heart needs to hear.</p>
      <Link to="/shop" data-testid="cart-empty-shop" className="inline-block mt-7 bg-[#8E9A86] text-white rounded-full px-8 py-3.5 font-medium hover:bg-[#7A8772] transition-colors">Browse the shop</Link>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
      <h1 className="font-heading text-5xl text-[#3A3632] mb-10">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
          {items.map((i) => (
            <CartItem key={i.lineKey} item={i} onUpdateQty={updateQty} onRemove={removeItem} />
          ))}

          {box && (
            <div className="bg-white rounded-xl border-2 border-[#8E9A86] p-5" data-testid="cart-custom-box">
              <div className="flex justify-between items-start">
                <h3 className="font-heading text-xl text-[#3A3632] flex items-center gap-2"><Gift className="h-5 w-5 text-[#8E9A86]" /> Your Custom Box</h3>
                <button onClick={removeBox} data-testid="cart-remove-box" className="text-[#8A8175] hover:text-[#C4A4A4]"><Trash2 className="h-4 w-4" /></button>
              </div>
              <ul className="mt-3 text-sm text-[#6B655F] space-y-1">
                {box.items.map((b) => <li key={b.id} className="flex justify-between"><span>{b.name}</span><span>{money(b.price)}</span></li>)}
              </ul>
              {box.gift_note && <p className="font-hand text-xl text-[#C4A4A4] mt-3">"{box.gift_note}"</p>}
            </div>
          )}
          <CartSetNudge />
          <CartAddons />
        </div>

        <CartSummary total={total} agreed={agreed} setAgreed={setAgreed} busy={busy} onReview={reviewOrder} />
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 bg-[#1A1A1A]/55 backdrop-blur-sm overflow-y-auto" data-testid="confirm-modal" onClick={() => !busy && setShowConfirm(false)}>
          <div className="bg-[#FAF7F2] w-full max-w-md border border-[#D9CBA0]/60 shadow-2xl my-auto max-h-[90dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-7 overflow-y-auto flex-1" data-testid="confirm-scroll-body">
              <span className="gh-overline">One last look</span>
              <h3 className="font-heading text-3xl text-[#3A3632] mt-2">Confirm your order</h3>
              <div className="gh-hairline my-5" />
              <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1" data-testid="confirm-items">
                {items.map((i) => (
                  <div key={i.lineKey} className="flex justify-between text-sm text-[#3A3632]">
                    <span>{i.name}{i.recipient ? ` (For a ${i.recipient})` : ""}{i.color ? ` · ${i.color}` : ""} <span className="text-[#8A8175]">× {i.quantity}</span></span>
                    <span>{money(i.price * i.quantity)}</span>
                  </div>
                ))}
                {box && (
                  <div className="flex justify-between text-sm text-[#3A3632]">
                    <span>Custom Box <span className="text-[#8A8175]">({box.items.length} items)</span></span>
                    <span>{money(boxTotal)}</span>
                  </div>
                )}
              </div>
              <div className="gh-hairline my-5" />
              <label className="block mt-4">
                <span className="text-sm text-[#3A3632] font-medium">Email for your receipt</span>
                <input type="email" required value={email} onChange={(e) => { setEmail(e.target.value); resetPromo(); }} data-testid="confirm-email" placeholder="you@email.com"
                  className="w-full mt-1.5 border border-[#D1CCC5] rounded-lg px-4 py-2.5 outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
              </label>
              {hasPhysical && (
              <label className="block mt-3">
                <span className="text-sm text-[#3A3632] font-medium">Your name <span className="text-[#8A8175] font-normal">(signs the Hope Card)</span></span>
                <input type="text" value={buyerName} maxLength={60} onChange={(e) => setBuyerName(e.target.value)} data-testid="buyer-name-input" placeholder="e.g. With love, Mom"
                  className="w-full mt-1.5 border border-[#D1CCC5] rounded-lg px-4 py-2.5 outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
                {buyerName.trim() && <p className="font-hand text-2xl text-[#C4A4A4] mt-1.5" data-testid="signature-preview">{buyerName.trim()}</p>}
              </label>
              )}
              {hasPhysical && (
              <div className="mt-3 rounded-xl border border-[#D9CBA0]/60 bg-white/60 p-4" data-testid="gift-receipt-section">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={giftReceipt} onChange={(e) => setGiftReceipt(e.target.checked)} data-testid="gift-receipt-toggle"
                    className="mt-0.5 h-4 w-4 accent-[#8E9A86]" />
                  <span>
                    <span className="text-sm font-medium text-[#3A3632] flex items-center gap-1.5"><Gift className="h-4 w-4 text-[#8E9A86]" /> Add a gift receipt</span>
                    <span className="block text-xs text-[#8A8175] mt-0.5">A printable receipt with <strong>no prices</strong> — so they never see the cost.</span>
                  </span>
                </label>
                {giftReceipt && (
                  <label className="block mt-3">
                    <span className="text-xs text-[#3A3632] font-medium">Gift message <span className="text-[#8A8175] font-normal">(optional)</span></span>
                    <textarea value={giftMessage} maxLength={300} rows={2} onChange={(e) => setGiftMessage(e.target.value)} data-testid="gift-message-input"
                      placeholder="e.g. Thinking of you always. 💛"
                      className="w-full mt-1.5 border border-[#D1CCC5] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30 resize-none" />
                    {giftMessage.trim() && <p className="font-hand text-xl text-[#C4A4A4] mt-1" data-testid="gift-message-preview">"{giftMessage.trim()}"</p>}
                  </label>
                )}
              </div>
              )}
              {hasPhysical && (
              <div className="mt-4 rounded-xl border border-[#D9CBA0]/60 bg-white/60 p-4" data-testid="shipping-section">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="h-4 w-4 text-[#8E9A86]" />
                  <span className="text-sm font-medium text-[#3A3632]">Shipping address & live rate</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <AddressAutocomplete
                    value={ship.street1}
                    onType={(v) => setShipField("street1", v)}
                    onSelect={onSelectAddress}
                    placeholder="Start typing your street address…"
                    testId="ship-street1" />
                  <input type="text" value={ship.city} onChange={(e) => setShipField("city", e.target.value)} data-testid="ship-city" placeholder="City"
                    className="border border-[#D1CCC5] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8E9A86]" />
                  <input type="text" value={ship.state} onChange={(e) => setShipField("state", e.target.value)} data-testid="ship-state" placeholder="State/Region"
                    className="border border-[#D1CCC5] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8E9A86]" />
                  <input type="text" value={ship.zip} onChange={(e) => setShipField("zip", e.target.value)} data-testid="ship-zip" placeholder="Postal code"
                    className="border border-[#D1CCC5] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8E9A86]" />
                  <select value={ship.country} onChange={(e) => setShipField("country", e.target.value)} data-testid="ship-country"
                    className="border border-[#D1CCC5] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8E9A86] bg-white">
                    {SHIP_COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                  </select>
                </div>
                {addrCheck && (
                  <div className={`mt-2 flex items-start gap-1.5 text-xs ${addrCheck.ok ? "text-[#8E9A86]" : "text-[#B4894A]"}`} data-testid="address-check">
                    {addrCheck.ok ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                    <span>{addrCheck.ok ? "Address verified." : (addrCheck.messages[0] || "Please double-check this address.")}</span>
                  </div>
                )}
                <button type="button" onClick={getShippingRate} disabled={shipBusy} data-testid="calc-shipping-btn"
                  className="w-full mt-2.5 bg-[#3A3632] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#2a2723] transition-colors disabled:opacity-50">
                  {shipBusy ? "Calculating live rate…" : shipQuote ? "Recalculate shipping" : "Calculate shipping"}
                </button>
                {shipQuote && shipQuote.amount > 0 && (
                  <p className="text-xs text-[#8E9A86] mt-2" data-testid="ship-rate-msg">
                    {money(shipQuote.amount)} · {shipQuote.provider} {shipQuote.servicelevel}
                    {shipQuote.estimated_days ? ` · est. ${shipQuote.estimated_days} days` : ""}
                  </p>
                )}
                <p className="text-[11px] text-[#8A8175] mt-2 leading-relaxed" data-testid="global-shipping-notice">
                  <strong>Global shipping:</strong> we ship worldwide. Rates are calculated live by carrier from your address & cart weight. International orders may take 10–20 business days and can incur customs fees on delivery.
                </p>
              </div>
              )}
              <label className="block mt-3">
                <span className="text-sm text-[#3A3632] font-medium">Promo code</span>
                <div className="flex gap-2 mt-1.5">
                  <input type="text" value={promoCode} onChange={(e) => { setPromoCode(e.target.value); resetPromo(); }} data-testid="promo-input" placeholder="WELCOME11"
                    className="flex-1 border border-[#D1CCC5] rounded-lg px-4 py-2.5 uppercase outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
                  <button type="button" onClick={applyPromo} disabled={promoBusy || !promoCode.trim()} data-testid="promo-apply-btn"
                    className="shrink-0 bg-[#3A3632] text-white rounded-lg px-5 font-medium hover:bg-[#2a2723] transition-colors disabled:opacity-50">{promoBusy ? "…" : "Apply"}</button>
                </div>
                {promoMsg && <p data-testid="promo-message" className={`text-xs mt-1.5 ${(promoPct || promoGift) ? "text-[#8E9A86]" : "text-[#C4A4A4]"}`}>{promoMsg}</p>}
              </label>
              {promoGift && (
                <div className="mt-3 flex items-center gap-3 bg-[#F3EEF7] border border-[#D8C7E6] rounded-xl p-3" data-testid="promo-gift-row">
                  <img src={ghHeart} alt="Hope & Favor complimentary gift" className="h-11 w-11 object-contain drop-shadow-sm" data-testid="promo-gift-heart" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#5B3A78]">{promoGift} added 💜</div>
                    <div className="text-xs text-[#8A8175]">A little thank-you, tucked into your order.</div>
                  </div>
                  <span className="text-sm font-semibold text-[#5B3A78]">{money(0)}</span>
                </div>
              )}
              <div className="gh-hairline my-5" />
              <div data-testid="cart-roundup">
                <div className="flex items-center gap-2"><Heart className="h-4 w-4 text-[#C4A4A4]" /><span className="text-sm font-medium text-[#3A3632]">Add a little extra hope</span></div>
                <p className="text-xs text-[#8A8175] mt-1">100% supports our Hope in Action outreach — no extra item is shipped to you.</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {DONATIONS.map((d) => (
                    <button key={d.amt} type="button" onClick={() => pickDonation(d)} data-testid={`roundup-${d.amt}`}
                      className={`text-xs font-medium rounded-full px-3.5 py-2 border transition-colors ${donation === d.amt ? "bg-[#8E9A86] text-white border-[#8E9A86]" : "bg-white text-[#3A3632] border-[#D1CCC5] hover:border-[#8E9A86]"}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="gh-hairline my-5" />
              {(promoPct > 0 || donation > 0 || shippingAmt > 0) && (
                <div className="flex justify-between items-center text-sm text-[#6B655F]">
                  <span>Subtotal</span><span>{money(total)}</span>
                </div>
              )}
              {promoPct > 0 && (
                <div className="flex justify-between items-center text-sm text-[#8E9A86] mt-1" data-testid="promo-discount-row">
                  <span>Discount ({promoPct}% — {promoCode.trim().toUpperCase()})</span>
                  <span>−{money(total - discountedTotal)}</span>
                </div>
              )}
              {donation > 0 && (
                <div className="flex justify-between items-center text-sm text-[#C4A4A4] mt-1" data-testid="roundup-row">
                  <span>{donationLabel}</span>
                  <span>+{money(donation)}</span>
                </div>
              )}
              {shippingAmt > 0 && (
                <div className="flex justify-between items-center text-sm text-[#6B655F] mt-1" data-testid="shipping-row">
                  <span>Shipping ({shipQuote.provider})</span>
                  <span>+{money(shippingAmt)}</span>
                </div>
              )}
              <p className="text-xs text-[#8A8175] leading-relaxed mt-3">{hasPhysical ? "A signature handwritten Hope Card is included, sent with care just for them. You'll be taken to our secure Stripe checkout to pay." : "Your printable download links will be shown right after checkout and emailed to you. You'll be taken to our secure Stripe checkout to pay."}</p>
            </div>
            <div className="border-t border-[#D9CBA0]/60 bg-[#F2EFEB] px-7 py-4 shrink-0" data-testid="confirm-footer">
              {familyNumber != null && (
                <p className="text-center text-xs text-[#5E6A56] font-semibold mb-2 flex items-center justify-center gap-1.5" data-testid="family-number-line">
                  <Heart className="h-3.5 w-3.5 fill-[#C4A4A4] text-[#C4A4A4]" /> You'll be family #{familyNumber.toLocaleString()} in the movement
                </p>
              )}
              <div className="flex justify-between items-center">
                <span className="text-[#6B655F]">Total</span>
                <div className="text-right">
                  <span data-testid="confirm-total" className="font-heading text-3xl text-[#8E9A86]">{money(finalTotal)}</span>
                  {localEstimate(finalTotal) && (
                    <div className="text-xs text-[#8A8175]" data-testid="local-estimate">≈ {localEstimate(finalTotal)} · <span className="italic">charged in USD</span></div>
                  )}
                </div>
              </div>
              <button onClick={checkout} disabled={busy || (hasPhysical && !shipQuote)} data-testid="confirm-pay-btn" className="w-full mt-3 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                {busy ? "Redirecting to secure checkout…" : (hasPhysical && !shipQuote) ? "Calculate shipping to continue" : `Confirm & Pay ${money(finalTotal)}`}
              </button>
              <button onClick={() => setShowConfirm(false)} disabled={busy} data-testid="confirm-back-btn" className="w-full mt-1.5 text-sm text-[#8A8175] hover:text-[#3A3632] py-1.5 disabled:opacity-50">Go back to cart</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
